import type { VoidComponent } from "solid-js";
import { createMemo, lazy, Show, untrack } from "solid-js";
import GlobalTabs from "../tabs/GlobalTabs";
import { HTMLProcessor } from "../../utils/processors/HtmlProcessor";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import { useTranslation } from "../../state/i18n";
import type { InlineMessageProps } from "../inlineMessage/InlineMessage";
import InlineMessage from "../inlineMessage/InlineMessage";
import { isNil } from "lodash-es";

type HtmlInputTab = "code" | "preview";

type HtmlInputProps = {
  ariaLabel?: string;
  value: string;
  onChange: (value: string) => void;
} & ExtendableInputProps;

type HtmlInputWithPreviewProps = {
  initialTab?: HtmlInputTab;
  message?: Pick<InlineMessageProps, "label" | "action">;
  allowStyleTag?: boolean;
} & HtmlInputProps;

const CodeMirror = lazy(() => import("../codeMirror/CodeMirror"));

export const HtmlInput: VoidComponent<HtmlInputProps> = (props) => (
  <Input {...props}>
    {(id) => (
      <div
        role="group"
        aria-label={props.ariaLabel ?? props.label}
        class="relative w-full overflow-hidden rounded-sm border"
        id={id}
        classList={{
          "border-default": isNil(props.errorMessage),
          "border-error": !isNil(props.errorMessage),
        }}
      >
        <div class="flex h-140 flex-col overflow-hidden">
          <CodeMirror value={props.value} onChange={props.onChange} />
        </div>
      </div>
    )}
  </Input>
);

export const HtmlInputWithPreview: VoidComponent<HtmlInputWithPreviewProps> = (props) => {
  const { t } = useTranslation();
  const processor = new HTMLProcessor(untrack(() => props.allowStyleTag ?? false));

  const sanitizedHtml = createMemo(() => {
    const sanitized = processor.clean(props.value);
    return processor.renderHtml(sanitized);
  });

  const InformativeMessage = () => (
    <Show when={props.message}>
      <div class="border-b border-default">
        <div class="m-2">
          <InlineMessage variant="informative" {...props.message} />
        </div>
      </div>
    </Show>
  );

  return (
    <Input {...props}>
      {(id) => (
        <div
          role="group"
          aria-label={props.ariaLabel ?? props.label}
          class="relative w-full overflow-hidden rounded-sm border"
          id={id}
          classList={{
            "border-default": isNil(props.errorMessage),
            "border-error": !isNil(props.errorMessage),
          }}
        >
          <GlobalTabs
            initialTab={props.initialTab ?? "code"}
            tabStyle="px-2 pt-1"
            items={[
              {
                label: () => t(($) => $.HTML_TAB_CODE_LABEL),
                key: "code",
                Slot: () => (
                  <div class="flex h-140 flex-col overflow-hidden">
                    <InformativeMessage />
                    <CodeMirror value={props.value} onChange={props.onChange} />
                  </div>
                ),
              },
              {
                label: () => t(($) => $.HTML_TAB_PREVIEW_LABEL),
                key: "preview",
                Slot: () => (
                  <div class="flex h-140 flex-col">
                    <InformativeMessage />
                    <article
                      // Scrollable region must have keyboard access (dequeuniversity.com/rules/axe/4.10/scrollable-region-focusable)
                      // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                      tabIndex={0}
                      class="prose max-w-none overflow-y-auto p-3 pb-6 wrap-break-word -outline-offset-1 focus-visible:outline-default"
                    >
                      {sanitizedHtml()}
                    </article>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    </Input>
  );
};
